import React from "react";
import { ShoppingBag } from "react-feather";
import SectionCard from "../../../components/common/SectionCard";
import { MAROON } from "../../../components/common/brandColors";
import { useGetTechnicalWorkOrderQuery } from "../technicalApi";

const num = (v) => Number(v) || 0;
const money = (n) => num(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;

/**
 * What the confirmed order sold, on an Installation job (2026-10-15: the order showed
 * "Automatic Alarm", who booked it and when, but the technical side saw only "Start Work").
 * Reads GET technical/work/{guid}/order, which is gated by the work item rather than
 * Sales read, so a technician sees it without holding any Sales permission. Prices
 * only reach the Technical Manager - the server strips them for everyone else.
 * Renders nothing for a complaint/service visit (no order behind it).
 */
const WorkOrderPanel = ({ recordGuid, hasOrder }) => {
  const { data, isLoading, isError } = useGetTechnicalWorkOrderQuery(recordGuid, { skip: !hasOrder });

  if (!hasOrder || isError) return null;

  // The server nulls every money field unless the caller is the Technical Manager
  // (Technical.ManageWork), so "any line has a rate/amount, or there is a total" is the switch.
  const showPrices = !!data && (data.total != null || data.lines.some((l) => l.rate != null || l.amount != null));

  return (
    <SectionCard
      icon={ShoppingBag}
      title="Order Details"
      right={data && <span className="text-muted" style={{ fontSize: 12 }}>{data.orderNumber}</span>}
    >
      {isLoading || !data ? (
        <span className="text-muted" style={{ fontSize: 13 }}>Loading order...</span>
      ) : (
        <>
          {data.lines.length === 0 ? (
            <p className="text-muted mb-2" style={{ fontSize: 13 }}>
              {data.servicesRequested || "No services listed on the order."}
            </p>
          ) : (
            <table className="table table-sm mb-2">
              <thead>
                <tr>
                  <th>Service</th>
                  <th className="text-end" style={{ width: 80 }}>Qty</th>
                  {showPrices && <th className="text-end">Rate</th>}
                  {showPrices && <th className="text-end">Amount</th>}
                </tr>
              </thead>
              <tbody>
                {data.lines.map((l, i) => (
                  <tr key={`${l.serviceTypeName}-${i}`}>
                    <td className="fw-semibold">{l.serviceTypeName}</td>
                    <td className="text-end">{num(l.qty) > 0 ? num(l.qty) : "-"}</td>
                    {showPrices && <td className="text-end">{l.rate != null ? money(l.rate) : "-"}</td>}
                    {showPrices && <td className="text-end">{l.amount != null ? money(l.amount) : "-"}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {data.total != null && (
            <div
              className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-2 mb-2 rounded"
              style={{ background: `${MAROON}0D`, fontSize: 13 }}
            >
              <span>
                Sub Total: <strong>{money(data.subTotal)}</strong>
                <span className="ms-3">VAT ({num(data.vatRate)}%): <strong>{money(data.vatAmount)}</strong></span>
              </span>
              <span>
                Total (incl. VAT): <strong style={{ color: MAROON, fontSize: 15 }}>{money(data.total)}</strong>
              </span>
            </div>
          )}

          <div className="text-muted" style={{ fontSize: 12.5 }}>
            {data.bookedByName && (
              <>Created by <strong>{data.bookedByName}</strong>{data.bookedDate && ` on ${formatDay(data.bookedDate)}`} &middot; </>
            )}
            {data.salespersonName && <>booked for <strong>{data.salespersonName}</strong> &middot; </>}
            order confirmed {formatDay(data.confirmedDate)}
          </div>

          {(data.area || data.customerAddress) && (
            <div className="mt-2" style={{ fontSize: 13 }}>
              <small className="text-muted d-block">Site</small>
              {[data.area, data.customerAddress].filter(Boolean).join(" - ")}
            </div>
          )}
          {data.remarks && (
            <div className="mt-2" style={{ fontSize: 13 }}>
              <small className="text-muted d-block">Order remarks</small>
              <span style={{ whiteSpace: "pre-wrap" }}>{data.remarks}</span>
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
};

export default WorkOrderPanel;
